import React, { useEffect, useRef, useState } from "react";
import { TURNSTILE_SITE_KEY } from "../../config/api";

const RUC_REGEX = /^\d{1,8}-\d$/;
const DOCNUM_REGEX = /^\d{3}-\d{3}-\d{7}$/;

const formatRuc = (input: string): string => {
  const digits = input.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 1) return digits;
  const body = digits.slice(0, digits.length - 1);
  const dv = digits.slice(-1);
  return `${body}-${dv}`;
};

const formatDocumentNumber = (input: string): string => {
  const digits = input.replace(/\D/g, "").slice(0, 13);
  const parts: string[] = [];
  if (digits.length > 0) parts.push(digits.slice(0, 3));
  if (digits.length > 3) parts.push(digits.slice(3, 6));
  if (digits.length > 6) parts.push(digits.slice(6, 13));
  return parts.join("-");
};

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      "timeout-callback"?: () => void;
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

interface Props {
  loading: boolean;
  onSubmit: (
    ruc: string,
    documentNumber: string,
    turnstileToken?: string,
  ) => void;
}

const LookupForm: React.FC<Props> = ({ loading, onSubmit }) => {
  const [ruc, setRuc] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const widgetContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const prevLoading = useRef(loading);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !widgetContainerRef.current) return;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile) {
        window.setTimeout(tryRender, 200);
        return;
      }
      if (widgetIdRef.current || !widgetContainerRef.current) return;
      widgetIdRef.current = window.turnstile.render(widgetContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY!,
        callback: (token: string) => setTurnstileToken(token),
        "error-callback": () => setTurnstileToken(null),
        "expired-callback": () => setTurnstileToken(null),
        "timeout-callback": () => setTurnstileToken(null),
      });
    };
    tryRender();
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Tokens are single-use; reset after each request completes.
    if (prevLoading.current && !loading) {
      setTurnstileToken(null);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    }
    prevLoading.current = loading;
  }, [loading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!RUC_REGEX.test(ruc)) {
      setError("RUC inválido. Use el formato xxxxxxxx-x.");
      return;
    }
    if (!DOCNUM_REGEX.test(docNumber)) {
      setError("Número inválido. Use el formato xxx-xxx-xxxxxxx.");
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Complete la verificación de seguridad antes de continuar.");
      return;
    }
    setError(null);
    onSubmit(ruc, docNumber, turnstileToken ?? undefined);
  };

  const submitDisabled =
    loading || (Boolean(TURNSTILE_SITE_KEY) && !turnstileToken);

  return (
    <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12 }}>
      <label style={{ display: "grid", gap: 4 }}>
        <span style={{ fontWeight: 600 }}>RUC del cliente</span>
        <input
          type="text"
          value={ruc}
          onChange={(e) => setRuc(formatRuc(e.target.value))}
          placeholder="80012345-6"
          inputMode="numeric"
          autoComplete="off"
          required
          style={inputStyle}
        />
      </label>
      <label style={{ display: "grid", gap: 4 }}>
        <span style={{ fontWeight: 600 }}>Número de documento</span>
        <input
          type="text"
          value={docNumber}
          onChange={(e) => setDocNumber(formatDocumentNumber(e.target.value))}
          placeholder="001-001-0000001"
          inputMode="numeric"
          autoComplete="off"
          required
          style={inputStyle}
        />
      </label>
      {TURNSTILE_SITE_KEY && <div ref={widgetContainerRef} />}
      {error && <div style={{ color: "var(--theme-error-text)" }}>{error}</div>}
      <button type="submit" disabled={submitDisabled} style={buttonStyle}>
        {loading ? "Buscando..." : "Buscar"}
      </button>
    </form>
  );
};

const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontSize: "1rem",
  border: "1px solid var(--theme-border)",
  borderRadius: 4,
  outline: "none",
};

const buttonStyle: React.CSSProperties = {
  padding: "10px 16px",
  fontSize: "1rem",
  fontWeight: 600,
  color: "#fff",
  background: "#006657",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
};

export default LookupForm;
